import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useIntl, useSelector } from 'umi';
import { useRequest } from 'ahooks';
import { flatMap, get, keyBy, map, uniq } from 'lodash-es';
import http from '@/utils/http';
import { useRequestController } from '@/pages/aigc/pages/Agent/Home/hooks/useRequestController.ts';
import { store } from '@/pages/aigc/pages/Agent/ChatPage/hooks/useLocalState';
import { COPILOT_EXTERNAL_QUERY_CONFIG_KEY } from '@/pages/aigc/pages/Agent/config/constant';
import type { ICopilotExternalQueryConfig } from '@/pages/aigc/pages/Agent/Copilot/hooks/useCopilotOpenBridge';
import type { OptionsItemType } from '@home/components/NewRegionSelect/type';
import { buildDashboardStyleGameLists, flattenAccessibleLeafGames, filterAccessibleMainGames } from '../utils/buildDashboardStyleGameLists';
import { mapDashboardChartChildren } from '../utils/mapDashboardChartChildren';
import { findGame, resolveGameSelectOption } from '../utils/gameSelectOption';
import useDashboardRouteGameCode from './useDashboardRouteGameCode';

/** 目标 page_type */
const TARGET_PAGE_TYPES = ['datalab', 'dashboard_custom'];

/** 递归展开 dashboard 树为扁平列表 */
const flattenDashboards = (list: any[] = []): any[] =>
  flatMap(list, (item) => [
    ...(TARGET_PAGE_TYPES.includes(item.page_type) ? [item] : []),
    ...flattenDashboards(item.dashoboard_list),
  ]);

/** listMenu + charts 转换后的最终结果（直接给 NewRegionSelect 使用） */
interface IGameDataResult {
  options: OptionsItemType[];
  /** 该 options 对应的 game_code，供外部判定数据是否已对齐目标游戏（解决切换游戏时的时序竞态） */
  gameCode: string;
}

/** 来源：自定义报表 AI 分析按钮（与 AIAnalysisButton 中传入的 form 值保持一致） */
const FORM_CUSTOM_DASHBOARD = 'custom_dashboard';

/** 外部入口自动勾选请求（每个面板生命周期内仅消费一次，不展开面板） */
interface IAutoSelectRequest {
  gameCode: string;
  dashboardValue: string;
}

/** 缓存有效期 30 分钟 */
const CACHE_TTL = 30 * 60 * 1000;

/** 空选项常量：避免 cache miss 时每次 render 创建新引用，触发下游 NewRegionSelect 的 useMemo 重算 */
const EMPTY_OPTIONS: OptionsItemType[] = [];

/**
 * 读取 Copilot 外部入口配置（不写入、不清空 store，避免影响其他消费者）。
 * - store 中可能存在 cleared 快照（Chat.tsx 消费后写入），优先取 cleared，再 fallback 到 raw 本身
 * - 调用方应在首次渲染读取快照，避免流程中途被清 store 影响
 */
const readExternalConfig = (): ICopilotExternalQueryConfig | null => {
  const raw = store.getItem<any>(COPILOT_EXTERNAL_QUERY_CONFIG_KEY, null);
  if (!raw) return null;
  return (raw.cleared as ICopilotExternalQueryConfig) ?? (raw as ICopilotExternalQueryConfig);
};

/**
 * ChartPopover 数据请求 Hook
 * - 游戏列表：useRequestController（cacheKey + cacheTime + staleTime 30min）
 * - 最近浏览：useRequestController（无缓存，每次挂载拉最新）
 * - listMenu & charts：useRequest 动态 cacheKey（`chart-game-data-${gameCode}`，30min 内绝不重发）
 * - 自定义报表入口（form === 'custom_dashboard' 且带 gameCode）：自动预选游戏并勾选当前页，不展开面板
 */
const useChartPopoverData = () => {
  const { locale } = useIntl();
  const routeGameCode = useDashboardRouteGameCode();
  const userInfo = useSelector(
    (state: any) => state.home?.userInfo,
    shallowEqual,
  );
  const userOpenId = userInfo?.userid;
  const [selectedGameCode, setSelectedGameCode] = useState<string>('');
  /** 上一次经分路由 gameCode，用于仅在路由变更时同步选中态 */
  const prevRouteGameCodeRef = useRef<string | null>(null);

  // ─── 自定义报表入口：首次渲染读取外部 gameCode，用于预选游戏 ───
  const [externalConfigSnapshot] = useState<ICopilotExternalQueryConfig | null>(() => readExternalConfig());
  const isFromCustomDashboard =
    externalConfigSnapshot?.form === FORM_CUSTOM_DASHBOARD
    && Boolean(externalConfigSnapshot?.gameCode);
  const externalGameCode = externalConfigSnapshot?.gameCode || '';
  const externalDashboardId = externalConfigSnapshot?.dashboardId || '';

  /** 外部自动勾选是否已消费（仅消费一次） */
  const [autoSelectConsumed, setAutoSelectConsumed] = useState(false);

  // ─── 1. 游戏列表（30min 内绝不重发：staleTime 命中跳过请求） ───
  const { data: gameData } = useRequestController({
    method: 'POST',
    url: '/api/v1/dashboard/userMessage',
    refreshDeps: useMemo(() => ({ style_version: 'v2' }), []),
    cacheTime: 1800000,
    staleTime: 1800000,
    cacheKey: 'new-chart-game-list',
  });

  // ─── 1.5 最近浏览的游戏列表（不缓存，保证分组与经分侧浏览记录同步） ───
  const { data: recentData } = useRequestController({
    method: 'POST',
    url: '/api/v1/dashboard/navigation_recent_view',
    refreshDeps: useMemo(() => ({ style_version: 'v2' }), []),
  });

  /** 最近浏览原始列表（保持接口返回顺序） */
  const recentListRaw: any[] = useMemo(() => (
    Array.isArray(recentData) ? recentData : []
  ), [recentData]);

  /** 主版本游戏列表（接口原始数据） */
  const mainGames: any[] = useMemo(
    () => get(gameData, 'main_game', []),
    [gameData],
  );

  /** 有权限的主版本树（listMenu / 触发器回显 / 默认选中共用） */
  const accessibleMainGames = useMemo(
    () => filterAccessibleMainGames(mainGames),
    [mainGames],
  );

  // ─── 2. listMenu + charts：动态 cacheKey 按 gameCode 分桶，30min 内绝不重发 ───
  const { data: gameDataResult, loading } = useRequest(
    async (): Promise<IGameDataResult | null> => {
      const game = findGame(selectedGameCode, accessibleMainGames);
      if (!game) return null;

      const menuResult: any = await http('/api/v2/dashboard/menu/listMenu', {
        method: 'POST',
        data: {
          user_id: userOpenId,
          game_code: game.game_code,
          studio_code: game.studio || '',
          role_id: game.role_id || '',
          type: game.role_type || '',
          third_party_game: game.third_party_game || '',
        },
        errorHandler: true,
      });

      const folders = flatMap(get(menuResult, 'menu_list', []), (m: any) => m.folder_list || []);
      // 同 dashboard 多 folder 挂载全部保留；charts 接口请求 uniq 一次避免重复 id
      const dashboardList = flatMap(folders, (f: any) => flattenDashboards(f.dashoboard_list));
      const dashboardIds = uniq(map(dashboardList, 'dashboard_custom_id').filter(Boolean));

      let dashboardsById: Record<string, any> = {};
      if (dashboardIds.length > 0) {
        const chartsResult: any = await http('/api/v1/datalab/dashboard/charts', {
          method: 'POST',
          data: { game_code: selectedGameCode, dashboard_ids: dashboardIds },
          errorHandler: true,
        });
        dashboardsById = keyBy(get(chartsResult, 'dashboards', []), 'dashboard_id');
      }

      // filter：以 chartsMap 为 truth source（兜底过滤 customLink 类伪 dashboard，charts:[] 视为合法空报表）
      // 字段拆分：value 跨 option 唯一防撞、realDashboardId 给后端协议
      // children.value 必带 optionValue 前缀，否则同 dashboard 多挂载下 children.value 重复会串联勾选
      const options: OptionsItemType[] = dashboardList
        .filter((item: any) => dashboardsById[item.dashboard_custom_id])
        .map((item: any, idx: number) => {
          const optionValue = `${item.dashboard_custom_id}__${item.page_value || ''}__${idx}`;
          const dash = dashboardsById[item.dashboard_custom_id];
          return {
            value: optionValue,
            realDashboardId: item.dashboard_custom_id,
            logDashboardTitleCN: String(
              dash?.dashboard_title || dash?.dashboard_title_cn || '',
            ),
            text: item.name_en || item.name_cn || '',
            textCN: item.name_cn || item.name_en || '',
            children: mapDashboardChartChildren(dash, optionValue),
          };
        });

      return { options, gameCode: selectedGameCode };
    },
    {
      ready: !!selectedGameCode && accessibleMainGames.length > 0,
      refreshDeps: [selectedGameCode, accessibleMainGames.length, userOpenId],
      // cacheKey 带 schema 版本号：options 结构有破坏性变化时必须 bump（ahooks cache 模块级 Map，HMR 不清）
      cacheKey: selectedGameCode ? `new-chart-game-data-v10-${selectedGameCode}` : undefined,
      cacheTime: CACHE_TTL,
      staleTime: CACHE_TTL,
    },
  );

  // ─── 3. 派生下拉选项 ───

  /** 经分 GameSelector 同款主版本列表 + 最近浏览 */
  const { gameList, recentList } = useMemo(
    () => buildDashboardStyleGameLists(mainGames, recentListRaw, locale),
    [mainGames, recentListRaw, locale],
  );

  /**
   * 可选叶子游戏（默认选中、外部入口校验共用）
   */
  const flatGames: any[] = useMemo(
    () => flattenAccessibleLeafGames(mainGames),
    [mainGames],
  );

  /** 默认选中：优先最近浏览中第一个可映射的叶子游戏 */
  const defaultGameCode = useMemo(() => {
    for (const recentGame of recentListRaw) {
      const recentCode = recentGame?.game_code;
      if (!recentCode) continue;
      const flatGame = flatGames.find((g: any) => g.game_code === recentCode);
      if (flatGame) return String(flatGame.game_code);
    }
    return String(flatGames[0]?.game_code || '');
  }, [recentListRaw, flatGames]);

  /** 当前选中游戏的触发器展示项 */
  const selectedGameOption = useMemo(
    () => resolveGameSelectOption(selectedGameCode, accessibleMainGames, locale),
    [selectedGameCode, accessibleMainGames, locale],
  );

  /** Dashboard + Charts 组合（NewRegionSelect 的 options） */
  const dashboardOptions: OptionsItemType[] = useMemo(
    () => gameDataResult?.options || EMPTY_OPTIONS,
    [gameDataResult?.options],
  );
  /** dashboardOptions 实际对应的 game_code（与 selectedGameCode 异步切换有时差） */
  const dashboardOptionsGameCode: string = gameDataResult?.gameCode || '';

  /** 外部入口：按 realDashboardId 匹配 dashboard option.value */
  const externalTargetDashboardValue: string = useMemo(() => {
    if (!isFromCustomDashboard || !externalDashboardId) return '';
    if (selectedGameCode !== externalGameCode) return '';
    const matched = dashboardOptions.find(
      (opt: any) => (opt.realDashboardId || opt.value) === externalDashboardId,
    );
    return matched?.value || '';
  }, [
    isFromCustomDashboard,
    externalDashboardId,
    externalGameCode,
    selectedGameCode,
    dashboardOptions,
  ]);

  /** 外部入口一次性自动勾选请求（数据就绪后产生，消费后为 null） */
  const autoSelectRequest: IAutoSelectRequest | null = useMemo(() => {
    if (autoSelectConsumed) return null;
    if (!externalTargetDashboardValue) return null;

    return {
      gameCode: externalGameCode,
      dashboardValue: externalTargetDashboardValue,
    };
  }, [autoSelectConsumed, externalTargetDashboardValue, externalGameCode]);

  /** 切换 Game：useRequest 通过 refreshDeps 自动按动态 cacheKey 触发/命中缓存 */
  const handleSelectGame = useCallback((gameCode: string) => {
    setSelectedGameCode(gameCode);
  }, []);

  /** 选中游戏：经分路由 gameCode 变更时同步；同一路由下允许手动切换其他游戏 */
  useEffect(() => {
    if (accessibleMainGames.length === 0) return;

    const validRouteCode = routeGameCode
      && findGame(routeGameCode, accessibleMainGames)
      ? routeGameCode
      : '';

    if (validRouteCode) {
      const prevRoute = prevRouteGameCodeRef.current;
      prevRouteGameCodeRef.current = validRouteCode;

      if (prevRoute !== validRouteCode) {
        setSelectedGameCode(validRouteCode);
      }
      return;
    }

    prevRouteGameCodeRef.current = null;

    if (selectedGameCode) return;

    const targetGameCode = isFromCustomDashboard && externalGameCode
      && flatGames.some((g: any) => g.game_code === externalGameCode)
      ? externalGameCode
      : defaultGameCode;

    if (targetGameCode) {
      setSelectedGameCode(targetGameCode);
    }
  }, [
    routeGameCode,
    selectedGameCode,
    defaultGameCode,
    accessibleMainGames,
    isFromCustomDashboard,
    externalGameCode,
    flatGames,
  ]);

  const consumeAutoSelectRequest = useCallback(() => {
    setAutoSelectConsumed(true);
    store.setItem(COPILOT_EXTERNAL_QUERY_CONFIG_KEY, null);
  }, []);

  return {
    gameList,
    recentList,
    mainGames: accessibleMainGames,
    selectedGameOption,
    defaultGameCode,
    /** 当前选中游戏的 dashboard + charts 列表（统一 checkbox 选择区域） */
    dashboardOptions,
    /** dashboardOptions 实际对应的 game_code（用于上层规避切换游戏的时序竞态） */
    dashboardOptionsGameCode,
    selectedGameCode,
    handleSelectGame,
    loading,
    autoSelectRequest,
    consumeAutoSelectRequest,
  };
};

export default useChartPopoverData;
